import { useState, useContext, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import Context from '../../../context';
import './index.scss';
import useKeypress from "../../../hooks/useKeypress";
import { buildDocPathEnd, getBackPath } from "../../../helpers";
import { TuneOutlined, PeopleAltOutlined } from "@mui/icons-material";
import CustomBadge from "../Badge";
import IconButton from "@mui/material/IconButton";
import Button from "../Buttons/Button";
import ListTitle from "../ListTitle";
import { getEwaBackPath } from "../../../libraries/ewa/helpers";
import { BACK_NAVIGATION_TYPE } from "../../../helpers/constants";

const DocumentList = props => {
  const [backButtonPath, setBackButtonPath] = useState(-1);
  const { viewMode, customHistory, itin } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const backButton = useRef(null);

  useLayoutEffect(() => {
    const defaultBackPath = getBackPath(location.pathname, customHistory);

    const ewaBackPath = getEwaBackPath();
    const backPath = (ewaBackPath?.returnTo && location.pathname === ewaBackPath?.entryPath) ? BACK_NAVIGATION_TYPE : defaultBackPath;
    setBackButtonPath(backPath);
  }, [customHistory, location.pathname]);

  const onKeyPressed = (doc, event, index) => {
    if (event.key === 'Enter') {
      if (doc?.type !== 'remote') {
        navigate(`${location.pathname}/${doc.type === 'folder' ? doc.id : `view/${doc.id}`}`);
      } else if (doc.remoteUrl) window.open(doc.remoteUrl, '_blank');
    }
  };

  const handleSpanClick = (doc, event) => {
    event.preventDefault();
    const pathEnd = buildDocPathEnd(doc, itin.documents);
    navigate(`${location.pathname}/${pathEnd}`);
  };

  useKeypress('Escape', backButton, () => {
    if (!backButton.current) return
    backButton.current.click();
    navigate(backButton.current)
  });

  const renderMyDocumentControlButtons = docId => (
    <div className="my-document-buttons"
         onClick={(e) => {
           e.stopPropagation()
         }}
    >
      <CustomBadge badgeContent={4}>
        <IconButton
          className="white-icon-button"
          aria-label="personalisation"
          sx={{
            transition: 'none',
            cursor: 'default',
          }}
          disableFocusRipple
          disableRipple
          tabIndex="-1"
        >
          <PeopleAltOutlined />
        </IconButton>
      </CustomBadge>
      <div className="vertical-separator"></div>
      <IconButton
        className="white-icon-button"
        aria-label="control"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`${location.pathname}/docs-permissions/${docId}`);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            navigate(`${location.pathname}/docs-permissions/${docId}`);
          }
        }}
      >
        <TuneOutlined />
      </IconButton>
    </div>
  );

  // TODO* Mydoc only if my docs are empty
  // TODO* Mydoc add file upload logic
  // TODO* Mydoc translation
  const renderUploadBlock = () => {
    const handleFileUpload = e => {
      console.log('handleFileUpload')
      const files = e.target.files;
      if (files.length > 0) {
        console.log('Selected files:', files);
      }
    };

    return (
      <div className="upload-hint-container">
        {viewMode === 'desktop' ? (
          <>
            <p>To start uploading documents to your trip click the button below</p>
            <Button onClick={props.getFiles}>Upload</Button>
          </>
        ) : (
          <>
            To start uploading documents to your trip,<br />
            tap the '+' icon above.
          </>
        )}
        <input
          type="file"
          accept="image/*, video/*, .pdf"
          ref={props.fileInputRef}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
      </div>
    );
  };

  const openWithHeaders = async (id, url, headers) => {
    window.open(url, '_blank');
    // [VNO-15975]
    // setSpinnerId(id);
    // try {
    //   const response = await fetch(url, { headers });
    //   const blob = await response.blob();
    //
    //   const windowUrl = URL.createObjectURL(blob);
    //   window.open(windowUrl, '_blank');
    //   setTimeout(() => URL.revokeObjectURL(windowUrl), 1000);
    // } catch (err) {
    //   console.error('Failed to open document:', err);
    // } finally {
    //   setSpinnerId(null);
    // }
  };

  return (
    <div className={`document-list document-list-${viewMode}`}>
      {props.showTitle && viewMode === 'desktop' && <ListTitle backButton={backButton} backButtonPath={backButtonPath} docScreenName={props?.docScreenName}/>}
      <ul>
        {
          props.data.map((x, i) => (
            <li
              key={`${props.section}-document-${i}`}
              tabIndex="0"
              onKeyDown={(event) => onKeyPressed(x, event, i)}
            >
              {props.section === 'my-documents' ? (
                <div
                  onClick={(e) => handleSpanClick(x, e, i)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="name">{x?.name}</span>
                  {x?.type === 'file' && renderMyDocumentControlButtons(x.id)}
                  {x?.type === 'folder' && <img src="/icons/chevron-down.png" alt="" />}
                </div>
              ) : (
                (x?.type !== 'remote')
                  ? (
                    <div
                      onClick={(e) => handleSpanClick(x, e, i)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="name">{x?.name}</span>
                      {x?.type === 'folder' && <img src="/icons/chevron-down.png" alt="" />}
                    </div>
                  ) : (x?.headers && typeof x.headers === 'object' && Object.keys(x.headers).length > 0) ? (
                    <span
                      className="name"
                      onClick={() => openWithHeaders(x.id, x?.remoteUrl, x?.headers)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/*[VNO-15975] {spinnerId === x.id ? <Spinner type="small" /> : x.name}*/}
                      {x.name}
                    </span>
                  ) : (
                    <a
                      href={x?.remoteUrl}
                      target="_blank"
                      rel="noreferrer"
                      tabIndex="-1"
                    >
                      <span className="name">{x.name}</span>
                    </a>
                  )
                )
              }
            </li>
          ))
        }
      </ul>
      {props.section === 'my-documents' && renderUploadBlock()}
    </div>
  );
};

export default DocumentList;